import React, { useDeferredValue, useState } from 'react'

import { ClosingDropdown, Badge, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { object, func, string, bool } from 'prop-types'

import WithTriangleUiContext from 'Pods/Shared/Hocs/WithTriangleUiContext'

import { EMPTY_OBJECT } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

import FiltersWrapperWithButtons from '../FiltersWrapperWithButtons'
import RemainingCardSizeSetting from './Components/RemainingCardSizeSetting/RemainingCardSizeSetting'
import RemainingCardVisibilitySettings
  from './Components/RemainingCardVisibilitySettings/RemainingCardVisibilitySettings'
import RemainingContentRestrictionFilters
  from './Components/RemainingContentRestrictionFilters/RemainingContentRestrictionFilters'
import RemainingFileFormatFilters from './Components/RemainingFileFormatFilters/RemainingFileFormatFilters'
import RemainingFiltersOverlay from './Components/RemainingFiltersOverlay/RemainingFiltersOverlay'
import RemainingMiscellaneousFilters from './Components/RemainingMiscellaneousFilters/RemainingMiscellaneousFilters'
import RemainingPolyCountFilters from './Components/RemainingPolyCountFilters/RemainingPolyCountFilters'
import RemainingPriceFilters from './Components/RemainingPriceFilters/RemainingPriceFilters'
import RemainingSortBySetting from './Components/RemainingSortBySetting/RemainingSortBySetting'
import getActiveFilterCount from './getActiveFilterCount'

function RemainingFilters({
  selectedFilters = EMPTY_OBJECT, getTrigger, locale = 'en', subscriptionFlowEnabled = false,
}) {
  const currentBreakpoint = useBreakpoint()
  const isMobile = ['xs', 'sm'].includes(currentBreakpoint)
  const [isOverlayOpen, setIsOverlayOpen] = useState(false)
  // The overlay frame paints in the tap's own frame; its filter groups (hundreds of components)
  // render right after as a deferred update, so the tap is not held until they finish.
  const isOverlayContentReady = useDeferredValue(isOverlayOpen)

  const activeFilterCount = getActiveFilterCount({ selectedFilters, currentBreakpoint, subscriptionFlowEnabled })

  function getContent(type) {
    return (
      <div className={classnames('remaining-filters__filters', `remaining-filters__filters-${type}`)}>
        {isMobile && <FiltersWrapperWithButtons type="modelType" />}
        {currentBreakpoint === 'xs' && <FiltersWrapperWithButtons type="designers" />}
        <RemainingPriceFilters />
        <RemainingPolyCountFilters locale={locale} />
        <RemainingContentRestrictionFilters />
        <RemainingMiscellaneousFilters />
        <RemainingSortBySetting />
        <RemainingFileFormatFilters />
        <RemainingCardSizeSetting />
        <RemainingCardVisibilitySettings />
      </div>
    )
  }

  function getBadge() {
    return (
      activeFilterCount > 0 && (
        <Badge type="warning" className="remaining-filters__badge">
          {activeFilterCount}
        </Badge>
      )
    )
  }

  function triggerOverlay() {
    setIsOverlayOpen(!isOverlayOpen)
  }

  const isButtonActive = activeFilterCount > 0 || isOverlayOpen

  if (isMobile) {
    return (
      <div className="remaining-filters remaining-filters--is-mobile">
        {getTrigger(triggerOverlay, isButtonActive)}
        {getBadge()}

        {isOverlayOpen && (
          <RemainingFiltersOverlay locale={locale} closeOverlay={triggerOverlay}>
            {isOverlayContentReady && getContent('mobile')}
          </RemainingFiltersOverlay>
        )}
      </div>
    )
  }

  return (
    <ClosingDropdown
      content={getContent('desktop')}
      position={{
        horizontal: 'left',
        vertical: 'bottom',
      }}
      className="remaining-filters"
      contentClassName="remaining-filters__content"
      isSimpleContent
      isSpaced
      size="large"
      onOpenChange={setIsOverlayOpen}
      withBackdrop
    >
      {getTrigger(null, isButtonActive)}
      {getBadge()}
    </ClosingDropdown>
  )
}

RemainingFilters.propTypes = {
  selectedFilters: object,
  getTrigger: func.isRequired,
  subscriptionFlowEnabled: bool,
  locale: string,
}

function mapStateToProps({ selectedFilters, locale, subscriptionFlowEnabled }) {
  return {
    selectedFilters,
    locale,
    subscriptionFlowEnabled,
  }
}

export default WithTriangleUiContext(mapStateToProps)(RemainingFilters)
